import { ref, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router/composables'
import { templateAppUrl } from '@/utils/templateApp'

type TemplateAppTrack = (
  eventName: string,
  params: Record<string, unknown>
) => void

/** track を渡さない導線（フッター・LP など）も GA4 で数えるための既定 */
const gtagTrack: TemplateAppTrack = (eventName, params) => {
  const gtag = (window as { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag !== 'function') return
  try {
    gtag('event', eventName, params)
  } catch {
    // 計測の失敗で起動を止めない
  }
}

/**
 * 書類テンプレートアプリ（別サイト）の起動。
 * カスタムトークンを varuna から発行し、テンプレートサイトへ渡して開く（ヤギッシュのログインを引き継ぐ）。
 *
 * マイページ・フッター・SP メニュー・サービス一覧・ヤギプライム LP・使いかたページなど
 * 複数導線から使うため composable に置く。source は導線ごとの利用数を GA4 で見るためのラベル。
 */
export function useTemplateApp(track: TemplateAppTrack = gtagTrack) {
  const router = useRouter()
  const vm = getCurrentInstance()!.proxy as any

  const loading = ref(false)
  const error = ref<string | null>(null)

  async function open(source: string, options: { sameTab?: boolean } = {}) {
    if (loading.value) return
    const trackParams = { source }
    track('template_app_open', trackParams)

    if (!vm.$firebase.isAuthenticated || !vm.$firebase.user) {
      // ログイン後に /template-app?auto=1 へ送り、そこでトークンを発行して開く（NewLogin.vue）
      router.push('/login?frompage=template')
      return
    }

    loading.value = true
    error.value = null
    // ポップアップブロック対策: クリック直後に空タブを確保し、トークン取得後にURLを流し込む
    const newTab = options.sameTab ? null : window.open('', '_blank')
    try {
      const formData = new FormData()
      formData.append('uuid', vm.$firebase.user.uid)
      // varuna は IDトークンの uid に対してのみカスタムトークンを発行する（uuid は照合用）
      const idToken = await vm.$firebase.user.getIdToken()
      const res = await vm.$axios.post(
        vm.$varuna.endPoints.getTemplateAppToken,
        formData,
        { headers: { Authorization: `Bearer ${idToken}` } }
      )
      const customToken = res.data.custom_token
      const url = `${templateAppUrl}/?customToken=${customToken}`
      if (options.sameTab) {
        window.location.href = url
      } else if (newTab) {
        newTab.location.href = url
      } else {
        window.open(url, '_blank')
      }
    } catch (e) {
      if (newTab) newTab.close()
      console.error('useTemplateApp open error:', e)
      track('template_app_open_error', trackParams)
      error.value = '起動に失敗しました。もう一度お試しください。'
    } finally {
      loading.value = false
    }
  }

  return { open, loading, error }
}
